<template>
  <div class="card">
    <div class="card-header bg-transparent border-0 pb-0">
      <h5 class="mb-1">{{ $t("forwards.overview.attention.title") }}</h5>
      <p class="text-body-tertiary fs-10 mb-0">
        {{ description ?? $t("forwards.overview.attention.description") }}
      </p>
    </div>
    <div class="card-body">
      <LoadingIndicatorComponent
        v-if="loading"
        :message="$t('forwards.overview.attention.loading')"
      />

      <RetryableErrorNoteComponent
        v-else-if="errorMessage"
        :message="errorMessage"
        @retry="emit('retry')"
      />

      <p v-else-if="rows.length === 0" class="text-body-tertiary mb-0 py-3">
        <FontAwesomeIcon icon="fa-solid fa-check-circle" class="me-2" />
        {{ $t("forwards.overview.attention.empty") }}
      </p>

      <ul v-else class="list-unstyled mb-0">
        <li
          v-for="(row, index) in rows"
          :key="`${row.kind}-${row.cta.kind}-${row.cta.id}-${index}`"
          class="attention-row d-flex align-items-start gap-3 py-3"
        >
          <div class="attention-status flex-shrink-0">
            <span
              class="badge rounded-pill text-wrap"
              :class="statusOf(row.kind).pillClass"
              data-testid="attention-status"
              >{{ $t(statusOf(row.kind).label) }}</span
            >
          </div>
          <div class="flex-grow-1">
            <MandateLinkComponent
              v-if="row.cta.kind === 'open_mandate'"
              :mandateId="row.cta.id"
              class="fw-semibold"
            >
              {{ row.subject }}
            </MandateLinkComponent>
            <RouterLink v-else :to="ctaTarget(row.cta)" class="fw-semibold">
              {{ row.subject }}
            </RouterLink>
            <div class="text-body-tertiary fs-9">{{ reasonOf(row) }}</div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import MandateLinkComponent from "@/forwards2/components/MandateLinkComponent.vue";
import RetryableErrorNoteComponent from "@/forwards2/components/RetryableErrorNoteComponent.vue";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { forwardsErrorMessage } from "@/forwards2/forwardsApi";
import type {
  AttentionCta,
  AttentionKind,
  AttentionRow,
  PeriodShortfall,
} from "@/forwards2/forwardsTypes";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import { computed } from "vue";
import { RouterLink, type RouteLocationRaw } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * What is waiting for the account holder: a mandate that is expected to fall short or whose escrow is
 * already missing for a period whose certificates should have been issued, a draft nobody confirmed, a
 * delivery short of escrow, escrow left behind by a closed deal — and, for staff only, a swap
 * delivery with no producer price, a confirmed deal's deliveries with no buyer
 * price, or a mandate with members that nightly allocation skips.
 * The dashboard's mandates panel (`MandatesTimelinePanelComponent`) lists its own rows through the same
 * card (a mandate still selling only at 20% contango).
 *
 * Each row states one problem and offers exactly one way in. The backend sends its own English
 * `reason`; rows of a kind this component knows are re-rendered from the numbers so the sentence
 * is translated, and anything whose numbers are missing falls back to the backend's sentence
 * rather than to a template with holes in it.
 */
const props = withDefaults(
  defineProps<{
    rows: AttentionRow[];
    loading?: boolean;
    error?: unknown;
    /** Replaces the overview's card description, for the mandates panel's own list. */
    description?: string | null;
  }>(),
  { loading: false, description: null }
);

const emit = defineEmits<{
  retry: [];
}>();

const { t } = useI18n();

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const WARNING_PILL = "bg-warning-subtle text-warning-emphasis";
const DANGER_PILL = "bg-danger-subtle text-danger-emphasis";
const NEUTRAL_PILL = "bg-secondary-subtle text-secondary-emphasis";

const STATUS_KEY = "forwards.overview.attention.status";

const STATUSES: Record<AttentionKind, { label: string; pillClass: string }> = {
  stale_draft: { label: `${STATUS_KEY}.stale_draft`, pillClass: NEUTRAL_PILL },
  open_slices: { label: `${STATUS_KEY}.open_slices`, pillClass: WARNING_PILL },
  escrow_shortfall: {
    label: `${STATUS_KEY}.escrow_shortfall`,
    pillClass: DANGER_PILL,
  },
  producer_price_missing: {
    label: `${STATUS_KEY}.producer_price_missing`,
    pillClass: WARNING_PILL,
  },
  delivery_unpriced: {
    label: `${STATUS_KEY}.delivery_unpriced`,
    pillClass: WARNING_PILL,
  },
  free_escrow: { label: `${STATUS_KEY}.free_escrow`, pillClass: NEUTRAL_PILL },
  infeasible: { label: `${STATUS_KEY}.infeasible`, pillClass: DANGER_PILL },
  mandate_escrow_missing: {
    label: `${STATUS_KEY}.mandate_escrow_missing`,
    pillClass: DANGER_PILL,
  },
  coarse_issuance: {
    label: `${STATUS_KEY}.coarse_issuance`,
    pillClass: WARNING_PILL,
  },
  allocation_not_ready: {
    label: `${STATUS_KEY}.allocation_not_ready`,
    pillClass: DANGER_PILL,
  },
  contango_discouraged: {
    label: `${STATUS_KEY}.contango_discouraged`,
    pillClass: WARNING_PILL,
  },
};

// A kind added by the backend after this file was written still has to render: it keeps a neutral
// pill and the backend's own sentence, rather than a raw translation key.
const FALLBACK_STATUS = {
  label: `${STATUS_KEY}.other`,
  pillClass: NEUTRAL_PILL,
};

const errorMessage = computed(() => {
  if (props.error === null || props.error === undefined) return null;
  return (
    forwardsErrorMessage(props.error) ??
    t("forwards.overview.attention.load_failed")
  );
});

const statusOf = (kind: AttentionKind) => STATUSES[kind] ?? FALLBACK_STATUS;

const reasonOf = (row: AttentionRow): string =>
  translatedReason(row) ?? row.reason;

/**
 * @returns the translated sentence, or null when the kind is unknown here or the row lacks the
 * numbers the sentence interpolates.
 */
function translatedReason(row: AttentionRow): string | null {
  if (!(row.kind in STATUSES)) return null;

  const key = `forwards.overview.attention.reason.${row.kind}`;

  if (row.kind === "contango_discouraged") return t(key);
  if (row.kind === "stale_draft") {
    return row.since === null ? null : t(key, { days: daysSince(row.since) });
  }
  if (row.kind === "free_escrow") {
    return row.mwh === null
      ? null
      : t(key, { mwh: formatNumberWithSpaces(row.mwh) });
  }
  if (row.kind === "producer_price_missing") {
    return row.since === null ? null : t(key, { date: formatDate(row.since) });
  }
  if (row.kind === "delivery_unpriced") {
    return row.delivery_count === null || row.mwh === null
      ? null
      : t(
          key,
          { mwh: formatNumberWithSpaces(row.mwh), count: row.delivery_count },
          row.delivery_count
        );
  }
  if (row.kind === "allocation_not_ready") {
    return row.company_names.length === 0
      ? null
      : t(key, { companies: row.company_names.join(", ") });
  }
  if (row.kind === "infeasible") return infeasibleReason(row.shortfalls);
  if (row.kind === "mandate_escrow_missing") {
    return row.period_label === null || row.mwh === null
      ? null
      : t(key, {
          period: row.period_label,
          mwh: formatNumberWithSpaces(row.mwh),
        });
  }
  if (row.kind === "coarse_issuance") {
    const issued = Object.entries(row.issuance_by_region);
    return row.listed_width === null ||
      row.issuance_width === null ||
      issued.length === 0
      ? null
      : t(key, {
          issued: issued
            .map(([region, width]) =>
              t("forwards.overview.attention.issued_in_region", {
                region,
                width: widthAdverb(width),
              })
            )
            .join(", "),
          listed: widthAdverb(row.listed_width),
          widest: widthAdverb(row.issuance_width),
        });
  }
  // `open_slices` and `escrow_shortfall` are totals over a deal and a delivery: the backend sends
  // no `period_label` for either, so the sentences name neither (RV-overview-06).
  return row.mwh === null
    ? null
    : t(key, { mwh: formatNumberWithSpaces(row.mwh) });
}

/** A single period reads as its own sentence; several are named in one. */
function infeasibleReason(shortfalls: PeriodShortfall[]): string | null {
  const [first] = shortfalls;
  if (first === undefined) return null;
  if (shortfalls.length === 1) {
    return t("forwards.overview.attention.reason.infeasible", {
      period: first.period_label,
      mwh: formatNumberWithSpaces(first.mwh),
    });
  }
  return t("forwards.overview.attention.reason.infeasible_periods", {
    periods: shortfalls
      .map(({ period_label, mwh }) =>
        t("forwards.overview.attention.period_mwh", {
          period: period_label,
          mwh: formatNumberWithSpaces(mwh),
        })
      )
      .join(", "),
  });
}

function widthAdverb(width: PeriodWidth): string {
  return t(`forwards.overview.attention.width_adverb.${width}`);
}

function daysSince(isoDate: string): number {
  const elapsed = Date.now() - new Date(isoDate).getTime();
  return Math.max(0, Math.floor(elapsed / MILLISECONDS_PER_DAY));
}

/**
 * Where a deal row's one action leads: the deal page, deep-linked to the delivery when the backend
 * named one, so "Deliver" lands on the delivery that is short. A mandate row opens the editor modal in
 * place instead.
 */
function ctaTarget(cta: AttentionCta): RouteLocationRaw {
  return dealRoute(cta.id, cta.delivery_id);
}
</script>

<style scoped lang="scss">
.attention-row + .attention-row {
  border-top: 1px solid var(--phoenix-border-color);
}

// A fixed column, so the subjects line up whatever each row's pill says.
.attention-status {
  width: 9rem;
}
</style>
